import { BLANK_VALUE, PAYMENT_METHOD } from '@/constants/analytics';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { APP_ROUTES } from '@/constants/pages';
import { Tracking } from '@/types/analytics';
import { CheckoutPaymentMethod } from '@/types/checkout';
import { captureMessage } from '@sentry/react';
import { matchPath } from 'react-router-dom';
import { amplitudeLogEvent, trackPageCio } from './analytics';
import { allowTracking } from './general';

export function getPaymentMethodLabel(paymentMethod: CheckoutPaymentMethod) {
  switch (paymentMethod) {
    case CHECKOUT_PAYMENT_METHOD.NONE:
      return BLANK_VALUE;
    case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE:
      return PAYMENT_METHOD.PAY_IN_SALON;
    case CHECKOUT_PAYMENT_METHOD.KLARNA:
      return PAYMENT_METHOD.KLARNA;
    case CHECKOUT_PAYMENT_METHOD.QLIRO:
      return PAYMENT_METHOD.QLIRO;
    case CHECKOUT_PAYMENT_METHOD.STORED_COF:
    case CHECKOUT_PAYMENT_METHOD.NEW_COF:
      return PAYMENT_METHOD.CARD;
    case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      return PAYMENT_METHOD.GOOGLE_PAY;
    case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
      return PAYMENT_METHOD.APPLE_PAY;
    case CHECKOUT_PAYMENT_METHOD.SWISH:
      return PAYMENT_METHOD.SWISH;
    default:
      let never: never = paymentMethod;
      console.error(`Unknown payment method: ${never}`);
      captureMessage(`[getPaymentMethodLabel] Unknown payment method: ${never}`);
      return PAYMENT_METHOD.PAY_IN_SALON;
  }
}

export const routeScreenNameMap: {
  pattern: (typeof APP_ROUTES)[keyof typeof APP_ROUTES];
  screen: Tracking.ScreenName;
}[] = [
  { pattern: APP_ROUTES.ABOUT, screen: 'about' },
  { pattern: APP_ROUTES.ADVICE_PAGES_ARCHIVE, screen: 'facts_and_advice' },
  { pattern: APP_ROUTES.BLOG, screen: 'blog' },
  { pattern: APP_ROUTES.BLOG_CATEGORY, screen: 'blog_category' },
  { pattern: APP_ROUTES.BLOG_LIST, screen: 'blog' },
  { pattern: APP_ROUTES.BLOG_SINGLE_POST, screen: 'blog_post' },
  { pattern: APP_ROUTES.BOOKING_CALENDAR, screen: 'booking_step_choose_time' },
  { pattern: APP_ROUTES.BOOKING_CHECKOUT, screen: 'checkout_booking_summary' },
  { pattern: APP_ROUTES.BOOKING_CHECKOUT_KLARNA, screen: 'checkout_booking_klarna_summary' },
  { pattern: APP_ROUTES.BOOKING_CHECKOUT_QLIRO, screen: 'checkout_booking_qliro_summary' },
  { pattern: APP_ROUTES.BOOKING_CONFIRMATION, screen: 'checkout_booking_confirmation' },
  { pattern: APP_ROUTES.BOOKING_PREPAY_CHECKOUT, screen: 'checkout_booking_update_payment_method' },
  { pattern: APP_ROUTES.BOOKING_PREPAY_CHECKOUT_KLARNA, screen: 'checkout_booking_klarna_update_payment_method' },
  { pattern: APP_ROUTES.BOOKING_PREPAY_CHECKOUT_QLIRO, screen: 'checkout_booking_qliro_update_payment_method' },
  { pattern: APP_ROUTES.BUNDLE_CHECKOUT, screen: 'checkout_bundle_summary' },
  { pattern: APP_ROUTES.BUNDLE_CHECKOUT_QLIRO, screen: 'checkout_bundle_qliro_summary' },
  { pattern: APP_ROUTES.BUNDLE_CONFIRMATION, screen: 'checkout_bundle_confirmation' },
  { pattern: APP_ROUTES.CANCEL_BY_CODE, screen: 'cancel_booking' },
  { pattern: APP_ROUTES.COOKIE_POLICY, screen: 'cookie_policy' },
  { pattern: APP_ROUTES.DEALS, screen: 'deals' },
  { pattern: APP_ROUTES.ETHICAL_POLICY, screen: 'ethical_policy' },
  { pattern: APP_ROUTES.GIFTCARD_CHECKOUT, screen: 'gift_card_landing_page' },
  { pattern: APP_ROUTES.GIFTCARD_USE, screen: 'check_giftcard_wellnesscard_value' },
  { pattern: APP_ROUTES.GOTO, screen: 'goto' },
  { pattern: APP_ROUTES.HOME, screen: 'home' },
  { pattern: APP_ROUTES.INJECTION_NOTICE, screen: 'injection_notice' },
  { pattern: APP_ROUTES.MY_BOKNINGS_DETAILS, screen: 'booking_details' },
  { pattern: APP_ROUTES.MY_BOOKINGS, screen: 'my_bookings_upcoming' },
  { pattern: APP_ROUTES.MY_BOOKINGS, screen: 'my_bookings_finished' },
  { pattern: APP_ROUTES.MY_BUNDLES_DETAILS, screen: 'bundle_details' },
  { pattern: APP_ROUTES.MY_BUNDLES_EXPIRED, screen: 'my_bundles_overview' },
  { pattern: APP_ROUTES.MY_BUNDLES_USED, screen: 'my_bundles_overview' },
  { pattern: APP_ROUTES.MY_BUNDLES_VALID, screen: 'my_bundles_overview' },
  { pattern: APP_ROUTES.MY_FAVORITES, screen: 'favorites' },
  { pattern: APP_ROUTES.PAYMENT_METHODS, screen: 'payment_methods' },
  { pattern: APP_ROUTES.PLACE_PAGE, screen: 'company_details' },
  { pattern: APP_ROUTES.PLACE_VALUECARD_BUY, screen: 'place_value_card_landing_page' },
  { pattern: APP_ROUTES.PORTAL, screen: 'portal' },
  { pattern: APP_ROUTES.PRIVACY_POLICY, screen: 'privacy_policy' },
  { pattern: APP_ROUTES.SEARCH_NOTICE, screen: 'search_notice' },
  { pattern: APP_ROUTES.SECURITY_ACKNOWLEDGEMENTS, screen: 'security_acknowledgements' },
  { pattern: APP_ROUTES.SECURITY_POLICY, screen: 'security_policy' },
  { pattern: APP_ROUTES.SERP, screen: 'search_results' },
  { pattern: APP_ROUTES.SISTA_MINUTEN_SERP, screen: 'deals' },
  { pattern: APP_ROUTES.SUPPORT, screen: 'support' },
  { pattern: APP_ROUTES.SUPPORT_FORM, screen: 'support_form' },
  { pattern: APP_ROUTES.TERMS, screen: 'terms_landing' },
  { pattern: APP_ROUTES.TERMS_BOOKING_CONSUMER, screen: 'terms' },
  { pattern: APP_ROUTES.TERMS_GIFTCARD_CONSUMER, screen: 'terms_giftcard' },
  { pattern: APP_ROUTES.TERMS_ONLINE_PAYMENT, screen: 'terms_online_payment' },
  { pattern: APP_ROUTES.WELLNESSCARD_CHECKOUT, screen: 'wellness_card_landing_page' },
];

export function getScreenNameFromPath(pathname: string): Tracking.ScreenName {
  for (const { pattern, screen } of routeScreenNameMap) {
    const match = matchPath(pathname, { path: pattern, exact: true });
    if (match) {
      return screen;
    }
  }
  return 'home'; // fallback
}

export function trackMpEvent({ name, properties }: Tracking.Event, sendToCio = true) {
  if (!allowTracking()) return;

  // send event to amplitude
  amplitudeLogEvent(name, { ...properties });

  if (!window.dataLayer) return;

  // send pageview to customer.io
  if (name === 'screen_shown' && sendToCio) {
    const params = { ...properties };
    const screenName = params.screen_name;
    delete params.screen_name;

    trackPageCio(screenName, params);
  }
}
